import { useState } from "react";
import {
  Badge,
  Button,
  DataTable,
  Select,
} from "@empite/ui-components";
import type {
  AudienceTier,
  ModelAudience,
  ModelPolicy,
  RoutingProfileProvider,
} from "../admin-client";
import { AUDIENCE_TIERS } from "../admin-client";
import {
  useSetModelAliasStatus,
  useSetModelAudience,
  useSetModelPolicyStatus,
} from "../admin-hooks";
import { ErrorAlert } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { FlagBadge, limit } from "./common";

/**
 * The alias — one client-facing model name and everything a routing profile
 * offers for it.
 *
 * This module exists because TWO screens render it. The routing-profile page
 * shows every alias a profile serves; the application page shows the same
 * aliases as "which models may this product call, and who has them yet". They
 * are the same rows read for two different questions, and a second copy of the
 * grouping would drift the first time one of them grew a column — with the
 * cheerful failure mode of two screens disagreeing about which alias is live.
 */

/** One alias and every route the profile offers for it, in the order
 *  resolution walks them. */
export interface AliasGroup {
  alias: string;
  policies: ModelPolicy[];
}

/**
 * Group the policies by the alias a client names.
 *
 * The alias is the unit an operator reasons about: a client asks for
 * `anthropic/claude-opus-5` and the profile answers with a CHAIN — Anthropic
 * first, OpenRouter behind it — and a flat table sorted by priority interleaves
 * three aliases' chains into one list where the fallback for any of them is
 * three rows away from the route it backs up.
 *
 * Inside a group the sort is `priority` alone, which is the resolution order:
 * ListRoutes orders by `mp.priority` then `rpp.priority`, and reordering the
 * provider chain RENUMBERS these ranks to follow (see the reorder's CTE), so
 * the second key never decides anything the first has not already decided.
 *
 * Groups themselves are alphabetical. There is no order between two aliases —
 * a request names one — so the only useful arrangement is the one an operator
 * can find a name in.
 */
export function groupByAlias(policies: ModelPolicy[]): AliasGroup[] {
  const byAlias = new Map<string, ModelPolicy[]>();
  for (const policy of policies) {
    const bucket = byAlias.get(policy.model);
    if (bucket) {
      bucket.push(policy);
    } else {
      byAlias.set(policy.model, [policy]);
    }
  }
  return [...byAlias.entries()]
    .map(([alias, rows]) => ({
      alias,
      policies: [...rows].sort((a, b) => a.priority - b.priority),
    }))
    .sort((a, b) => a.alias.localeCompare(b.alias));
}

/**
 * Whether this profile's chain can actually reach the provider a policy names.
 *
 * ListRoutes joins the policy to the chain on `accounts.provider =
 * policies.provider` and then filters the account down to enabled, active and
 * not-down. A policy naming a provider the chain cannot supply yields no row
 * at all: the route is not refused, it simply is not there, and on a screen
 * that showed only `allowed` it looks identical to one that works.
 */
export function reachable(chain: RoutingProfileProvider[], provider: string): boolean {
  return chain.some(
    (entry) =>
      entry.provider === provider &&
      entry.enabled &&
      entry.account_status === "active" &&
      entry.health !== "down",
  );
}

/**
 * Reads an alias's ring out of the list a profile document carries.
 *
 * The list holds ONLY restricted aliases, so a lookup that misses means "live"
 * — the same reading the resolver's `coalesce(audience, 'live')` makes. It is a
 * function returned from a Map rather than a `.find()` per alias so a profile
 * with two hundred aliases does not become quadratic on a page that renders all
 * of them.
 */
export function audienceLookup(audiences: ModelAudience[]): (alias: string) => AudienceTier {
  const byAlias = new Map(audiences.map((entry) => [entry.model, entry.audience]));
  return (alias) => byAlias.get(alias) ?? "live";
}

/**
 * How a release ring is described to an operator.
 *
 * The words are not the wire values: `internal` is a decision about our own
 * team, and "Empite only" says that where "internal" could be read as anything
 * from a network boundary to a build flag. The wire value is what the server
 * takes; this is what a person reads.
 */
const AUDIENCE_LABELS: Record<AudienceTier, string> = {
  internal: "Empite only",
  tester: "External testers",
  live: "Everyone",
};

const AUDIENCE_DESCRIPTIONS: Record<AudienceTier, string> = {
  internal:
    "Only Empite developers and subjects this application has put in the internal ring can " +
    "see or run it. To everybody else the model does not exist — a request for it is refused " +
    "exactly as one this application never permitted would be, so an unreleased model " +
    "cannot be found by guessing at names.",
  tester:
    "Empite developers and this application's external testers can run it. Ordinary " +
    "customers cannot see it in their model list and cannot call it.",
  live: "Everybody this application serves can see and run it. This is the default.",
};

/**
 * The audience an alias is released to, with the control that changes it.
 *
 * `undefined` — no routing.model_audiences row — is rendered as `live` and not
 * as "unset". That is what the resolver reads it as, and a console that showed
 * a third state would be describing a distinction the request path does not
 * make.
 */
export function AudienceControl({
  alias,
  label,
  profileId,
  audience,
  canWrite,
  onDone,
}: {
  /** The name the SERVER is keyed on. Sent, never shown. */
  alias: string;
  /**
   * What to call it to the operator, defaulting to the alias.
   *
   * The routing-profile page leaves it alone: there the alias IS the subject,
   * and a block headed by it should confirm with it. The application page
   * passes the model's display name, because that screen deliberately does not
   * put alias vocabulary in front of somebody who only wants to say which
   * products may use which models.
   */
  label?: string;
  profileId: string;
  audience: AudienceTier;
  canWrite: boolean;
  onDone: (message: string) => void;
}) {
  const shown = label ?? alias;
  const setAudience = useSetModelAudience();
  // The pending choice is held here rather than written on change: this is a
  // release, so it goes through the reason prompt like every other write on
  // this console, and a Select that saved on change would have no room for one.
  const [choice, setChoice] = useState<AudienceTier>(audience);
  const dirty = choice !== audience;

  if (!canWrite) {
    return (
      <Badge tone={audience === "live" ? "success" : "warning"}>{AUDIENCE_LABELS[audience]}</Badge>
    );
  }

  return (
    <div className="esm-actions">
      <Select
        aria-label={`Who may use ${shown}`}
        value={choice}
        onChange={(event) => setChoice(event.target.value as AudienceTier)}
      >
        {AUDIENCE_TIERS.map((tier) => (
          <option key={tier} value={tier}>
            {AUDIENCE_LABELS[tier]}
          </option>
        ))}
      </Select>
      {dirty ? (
        <ReasonButton
          variant="primary"
          busy={setAudience.isPending && setAudience.variables?.alias === alias}
          mutation={{
            title: `Release ${shown} to ${AUDIENCE_LABELS[choice].toLowerCase()}`,
            tone: "primary",
            confirmLabel: "Change who may use it",
            description: (
              <>
                <p>
                  <strong>{shown}</strong> moves from{" "}
                  <strong>{AUDIENCE_LABELS[audience].toLowerCase()}</strong> to{" "}
                  <strong>{AUDIENCE_LABELS[choice].toLowerCase()}</strong> for this application.
                </p>
                <p>{AUDIENCE_DESCRIPTIONS[choice]}</p>
              </>
            ),
            run: async (reason) => {
              await setAudience.mutateAsync({
                profileID: profileId,
                alias,
                audience: choice,
                reason,
              });
              onDone(`${shown} is now available to ${AUDIENCE_LABELS[choice].toLowerCase()}.`);
            },
          }}
        >
          Save
        </ReasonButton>
      ) : null}
    </div>
  );
}

export function AliasBlock({
  group,
  chain,
  profileId,
  audience,
  canWrite,
  onAdd,
  onEdit,
  onDone,
}: {
  group: AliasGroup;
  chain: RoutingProfileProvider[];
  profileId: string;
  /** Which ring this alias is released to. `live` when nothing restricts it —
   *  see AudienceControl for why that is not a third state. */
  audience: AudienceTier;
  canWrite: boolean;
  onAdd: () => void;
  /** Omitted on a screen that only reads the routes, which hides the per-row
   *  edit and archive controls with it. */
  onEdit?: (policy: ModelPolicy) => void;
  onDone: (message: string) => void;
}) {
  const setStatus = useSetModelPolicyStatus();
  // Read here only for the banner: the control itself owns the mutation, and
  // the refusal belongs at the top of the alias it is about rather than inside
  // a Select.
  const setAudience = useSetModelAudience();
  const setAliasStatus = useSetModelAliasStatus();
  const live = group.policies.filter(
    (policy) => policy.allowed && reachable(chain, policy.provider),
  );
  // Off for this application when NO route is still permitted. Derived rather
  // than stored: `allowed` is the only gate, so a second flag saying the same
  // thing would be a second way for a model to be off, and "off for two
  // different reasons" is a state nobody can reason about from a screen.
  const disabled = group.policies.every((policy) => !policy.allowed);

  return (
    <section className="esm-alias-block">
      <h3>
        <code>{group.alias}</code>{" "}
        {disabled ? <Badge tone="danger">off for this application</Badge> : null}
      </h3>
      <ErrorAlert error={setAudience.error} title="Who may use this could not be changed" />
      <ErrorAlert error={setAliasStatus.error} title="That model could not be switched" />
      <div className="esm-form-grid">
        <div>
          <strong>Availability</strong>
          <AudienceControl
            alias={group.alias}
            profileId={profileId}
            audience={audience}
            canWrite={canWrite}
            onDone={onDone}
          />
        </div>
      </div>
      <p className="esm-muted-text">
        {live.length === 0 ? (
          <>
            Nothing serves this alias: every route below is either archived or names a provider
            this profile's chain cannot supply. A request for it fails.
          </>
        ) : (
          <>
            {live.length === 1
              ? "One route serves this alias."
              : `${live.length} routes, tried in this order.`}{" "}
            The first that answers serves the request.
          </>
        )}
      </p>
      <ErrorAlert error={setStatus.error} title="That policy could not be changed" />
      <DataTable<ModelPolicy>
        rows={group.policies}
        rowKey={(row) => row.id}
        empty="No route."
        columns={[
          { key: "priority", header: "#", align: "end", render: (row) => row.priority },
          {
            key: "target",
            header: "Served by",
            // The pair is the mapping: what a customer asks for, and what the
            // router actually calls on their behalf.
            render: (row) => (
              <>
                <div>
                  <code>{row.provider_model}</code>
                </div>
                <span className="esm-muted-text">{row.provider}</span>
              </>
            ),
          },
          {
            key: "allowed",
            header: "Route",
            render: (row) => (
              <div className="esm-badge-row">
                <Badge tone={row.allowed ? "success" : "danger"}>
                  {row.allowed ? "allowed" : "archived"}
                </Badge>
                {/* Stated only when the route is otherwise live: on an
                    archived policy it is noise, because the policy is not
                    being tried either way. */}
                {row.allowed && !reachable(chain, row.provider) ? (
                  <Badge tone="warning">no provider in this chain</Badge>
                ) : null}
              </div>
            ),
          },
          {
            key: "tokens",
            header: "In / out / thinking",
            align: "end",
            render: (row) =>
              `${limit(row.max_input_tokens)} / ${limit(row.max_output_tokens)} / ${limit(row.max_thinking_tokens)}`,
          },
          {
            key: "features",
            header: "Features",
            render: (row) => (
              <div className="esm-badge-row">
                <FlagBadge on={row.allow_tools} label="tools" />
                <FlagBadge on={row.allow_vision} label="vision" />
                <FlagBadge on={row.allow_prompt_caching} label="caching" />
                <FlagBadge on={row.allow_reasoning} label="reasoning" />
              </div>
            ),
          },
          ...(canWrite && onEdit
            ? [
                {
                  key: "actions",
                  header: "",
                  align: "end" as const,
                  render: (row: ModelPolicy) => (
                    <div className="esm-actions">
                      <Button variant="ghost" onClick={() => onEdit(row)}>
                        Edit
                      </Button>
                      <ReasonButton
                        variant={row.allowed ? "danger" : "secondary"}
                        busy={setStatus.isPending && setStatus.variables?.policyID === row.id}
                        mutation={{
                          title: row.allowed
                            ? `Archive ${group.alias} on ${row.provider}`
                            : `Restore ${group.alias} on ${row.provider}`,
                          tone: row.allowed ? "danger" : "primary",
                          description: row.allowed ? (
                            <p>
                              This profile stops offering <code>{group.alias}</code> through{" "}
                              <code>{row.provider_model}</code> on <code>{row.provider}</code>.
                              {group.policies.filter((p) => p.allowed).length === 1
                                ? " It is the last route for this alias, so a request naming it will fail."
                                : " Requests fall through to the next route for this alias."}{" "}
                              The row stays, at this rank, so the decision is readable later.
                            </p>
                          ) : (
                            <p>
                              <code>{group.alias}</code> starts resolving through{" "}
                              <code>{row.provider_model}</code> again, at rank {row.priority}.
                            </p>
                          ),
                          confirmLabel: row.allowed ? "Archive route" : "Restore route",
                          run: async (reason) => {
                            await setStatus.mutateAsync({
                              profileID: profileId,
                              policyID: row.id,
                              status: row.allowed ? "disabled" : "active",
                              reason,
                            });
                            onDone(
                              row.allowed
                                ? `${group.alias} no longer resolves through ${row.provider}.`
                                : `${group.alias} resolves through ${row.provider} again.`,
                            );
                          },
                        }}
                      >
                        {row.allowed ? "Archive" : "Restore"}
                      </ReasonButton>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
      {canWrite ? (
        <div className="esm-actions">
          <Button variant="secondary" onClick={onAdd}>
            Add a fallback for {group.alias}
          </Button>
          {/* The MODEL's off switch, as against the per-route Archive in the
              table above. Archiving each provider in turn is how a model ends
              up half-disabled — still answering through the route somebody
              missed — so this moves the whole name at once. */}
          <ReasonButton
            variant={disabled ? "secondary" : "danger"}
            busy={setAliasStatus.isPending}
            mutation={{
              title: disabled
                ? `Enable ${group.alias} for this application`
                : `Disable ${group.alias} for this application`,
              tone: disabled ? "primary" : "danger",
              confirmLabel: disabled ? "Enable" : "Disable",
              description: disabled ? (
                <>
                  <p>
                    This application's clients can call <code>{group.alias}</code> again.
                  </p>
                  <p>
                    Every route below starts serving, <strong>including any you archived on its
                    own</strong> — this switch does not remember which ones it turned off.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    This application's clients stop being able to call <code>{group.alias}</code>.
                    All {group.policies.length} of its routes stop at once, so it cannot keep
                    answering through one you missed.
                  </p>
                  <p>
                    The rows stay, so this is reversible and the decision is on the record. The
                    model itself is untouched — every other application keeps it.
                  </p>
                </>
              ),
              run: async (reason) => {
                const result = await setAliasStatus.mutateAsync({
                  profileID: profileId,
                  alias: group.alias,
                  status: disabled ? "active" : "disabled",
                  reason,
                });
                onDone(
                  disabled
                    ? `${group.alias} is available to this application again (${result.routes_changed} route(s) restored).`
                    : `${group.alias} is off for this application (${result.routes_changed} route(s) stopped).`,
                );
              },
            }}
          >
            {disabled ? "Enable for this application" : "Disable for this application"}
          </ReasonButton>
        </div>
      ) : null}
    </section>
  );
}

